/* eslint-disable max-len */
import { Row } from 'antd';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/fields/InputField';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import { VehicleColumn } from '../shared';
import EquipmentSection from './EquipmentSection';
import FuelConsumptionSection from './FuelConsumptionSection';
import LocalizeValueSection from './LocalizeValueSection';
import NumberUnitSection from './NumberUnitSection';

type VehicleDetailsProps = {
    isPartnerNumberSetup: boolean;
};

const VehicleDetails = ({ isPartnerNumberSetup }: VehicleDetailsProps) => {
    const { t } = useTranslation('finderVehicle');

    const { yesNoSwitch } = useSystemSwitchData();

    const itemProps = useMemo(() => ({ isError: !isPartnerNumberSetup }), [isPartnerNumberSetup]);

    return (
        <Row gutter={14}>
            <VehicleColumn>
                <FormFields.InputField label={t('finderVehicle:fields.listing.id.label')} name="listing.id" disabled />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.marketplaceId.label')}
                    name="listing.marketplaceId"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.stockNumber.label')}
                    name="listing.stockNumber"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.SwitchField
                    {...t('finderVehicle:fields.listing.additionalCustomerInformationAvailable', {
                        returnObjects: true,
                    })}
                    name="listing.additionalCustomerInformationAvailable"
                    {...yesNoSwitch}
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.DatePickerField
                    {...t('finderVehicle:fields.listing.availableFromValue', {
                        returnObjects: true,
                    })}
                    name="listing.availableFrom.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.onlineOrderableState.label')}
                    name="listing.onlineOrderableState"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceCurrencyCode.label')}
                    name="listing.price.currencyCode"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceLocalize.label')}
                    name="listing.price.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceRecycleFeeTypeLocalize.label')}
                    name="listing.price.recycleFeeType.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceRecycleFeeTypeValue.label')}
                    name="listing.price.recycleFeeType.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceValue.label')}
                    name="listing.price.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.priceVatDeductible.label')}
                    name="listing.price.vatDeductible"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerAddressLocalize.label')}
                    name="listing.seller.address.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerEmail.label')}
                    name="listing.seller.email"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerId.label')}
                    name="listing.seller.id"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerNameLocalize.label')}
                    name="listing.seller.name.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerPhoneNumber.label')}
                    name="listing.seller.phoneNumber"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    itemProps={itemProps}
                    label={t('finderVehicle:fields.listing.sellerPorschePartnerNumber.label')}
                    name="listing.seller.porschePartnerNumber"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleVin.label')}
                    name="listing.vehicle.vin"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleCommissionNumber.label')}
                    name="listing.vehicle.commissionNumber"
                    disabled
                />
            </VehicleColumn>
            <LocalizeValueSection
                formPrefix="listing.vehicle.modelSeries"
                labelPrefix="finderVehicle:fields.listing.vehicleModelSeries"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.modelCategory"
                labelPrefix="finderVehicle:fields.listing.vehicleModelCategory"
            />
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleModelYear.label')}
                    name="listing.vehicle.modelYear"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.DatePickerField
                    label={t('finderVehicle:fields.listing.vehicleFirstRegistrationDateValue.label')}
                    name="listing.vehicle.firstRegistrationDate.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleFirstRegistrationDateLocalize.label')}
                    name="listing.vehicle.firstRegistrationDate.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleName.label')}
                    name="listing.vehicle.name.localize"
                    disabled
                />
            </VehicleColumn>
            <NumberUnitSection
                formPrefix="listing.vehicle.mileage"
                labelPrefix="finderVehicle:fields.listing.vehicleMileage"
            />
            <NumberUnitSection
                formPrefix="listing.vehicle.power.horsepower"
                labelPrefix="finderVehicle:fields.listing.vehiclePowerHorsePower"
            />
            <NumberUnitSection
                formPrefix="listing.vehicle.power.kilowatt"
                labelPrefix="finderVehicle:fields.listing.vehiclePowerKilowatt"
            />
            <VehicleColumn>
                <InputField
                    label={t('finderVehicle:fields.listing.vehiclePowerLocalizeValue.label')}
                    name="listing.vehicle.power.localizeValue"
                    disabled
                />
            </VehicleColumn>
            <NumberUnitSection
                formPrefix="listing.vehicle.displacement.cubicCentimeter"
                labelPrefix="finderVehicle:fields.listing.vehicleDisplacementCubicCentimeter"
            />
            <NumberUnitSection
                formPrefix="listing.vehicle.displacement.liter"
                labelPrefix="finderVehicle:fields.listing.vehicleDisplacementLiter"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.transmission"
                labelPrefix="finderVehicle:fields.listing.vehicleTransmission"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.engineType"
                labelPrefix="finderVehicle:fields.listing.vehicleEngineType"
            />
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleNumberOfCylinders.label')}
                    name="listing.vehicle.numberOfCylinders"
                    disabled
                />
            </VehicleColumn>
            <LocalizeValueSection
                formPrefix="listing.vehicle.fuelType"
                labelPrefix="finderVehicle:fields.listing.vehicleFuelType"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.bodyType"
                labelPrefix="finderVehicle:fields.listing.vehicleBodyType"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.drivetrain"
                labelPrefix="finderVehicle:fields.listing.vehicleDrivetrain"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.exteriorColor.colorGroup"
                labelPrefix="finderVehicle:fields.listing.vehicleExteriorColorColorGroup"
            />
            <VehicleColumn>
                <FormFields.SwitchField
                    {...t('finderVehicle:fields.listing.vehicleExteriorColorMetallic', {
                        returnObjects: true,
                    })}
                    name="listing.exteriorColor.metallic"
                    {...yesNoSwitch}
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleExteriorColorNameLocalize.label')}
                    name="listing.vehicle.exteriorColor.name.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleExteriorColorValue.label')}
                    name="listing.vehicle.exteriorColor.value"
                    disabled
                />
            </VehicleColumn>
            <LocalizeValueSection
                formPrefix="listing.vehicle.interior.color.colorGroup"
                labelPrefix="finderVehicle:fields.listing.vehicleInteriorColorColorGroup"
            />
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleInteriorColorSeatColorHexCode.label')}
                    name="listing.vehicle.interior.color.seatColorHexCode"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleInteriorNameLocalize.label')}
                    name="listing.vehicle.interior.name.localize"
                    disabled
                />
            </VehicleColumn>
            <LocalizeValueSection
                formPrefix="listing.vehicle.interior.material"
                labelPrefix="finderVehicle:fields.listing.vehicleInteriorMaterial"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.rooftopColor.colorGroup"
                labelPrefix="finderVehicle:fields.listing.vehicleRooftopColorColorGroup"
            />
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleRooftopColorNameLocalize.label')}
                    name="listing.vehicle.rooftopColor.name.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleRooftopColorValue.label')}
                    name="listing.vehicle.rooftopColor.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.vehicleNumberOfPreviousOwners.label')}
                    name="listing.vehicle.numberOfPreviousOwners.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.SwitchField
                    {...t('finderVehicle:fields.listing.vehicleAccidentFree', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.accidentFree"
                    {...yesNoSwitch}
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.DatePickerField
                    {...t('finderVehicle:fields.listing.vehicleNextGeneralInspectionValue', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.nextGeneralInspection.value"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    {...t('finderVehicle:fields.listing.vehicleNextGeneralInspectionLocalize', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.nextGeneralInspection.localize"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    {...t('finderVehicle:fields.listing.vehicleOrderTypeCode', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.orderTypeCode"
                    disabled
                />
            </VehicleColumn>
            <EquipmentSection />
            <LocalizeValueSection
                formPrefix="listing.vehicle.energyEfficiency.france.critAir"
                labelPrefix="finderVehicle:fields.listing.vehicleEnergyEfficiencyFranceCritAir"
            />
            <VehicleColumn>
                <FormFields.InputField
                    {...t('finderVehicle:fields.listing.vehicleEnergyEfficiencyFranceEnergyEfficiencyClass', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.energyEfficiency.france.energyEfficiencyClass"
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    {...t('finderVehicle:fields.listing.vehicleEnergyEfficiencySwitzerlandEnergyEfficiencyClass', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.energyEfficiency.switzerland.energyEfficiencyClass"
                    disabled
                />
            </VehicleColumn>
            <NumberUnitSection
                formPrefix="listing.vehicle.unladenWeight.kilogramm"
                labelPrefix="finderVehicle:fields.listing.vehicleUnladenWeightKilogramm"
            />
            <NumberUnitSection
                formPrefix="listing.vehicle.unladenWeight.pound"
                labelPrefix="finderVehicle:fields.listing.vehicleUnladenWeightPound"
            />
            <FuelConsumptionSection
                formPrefix="listing.vehicle.consumptionEmission.wltp.fuelConsumption.combined"
                labelPrefix="finderVehicle:fields.listing.vehicleConsumptionEmissionWltpFuelConsumptionCombined"
            />
            <FuelConsumptionSection
                formPrefix="listing.vehicle.consumptionEmission.wltp.fuelConsumption.extraHigh"
                labelPrefix="finderVehicle:fields.listing.vehicleConsumptionEmissionWltpFuelConsumptionExtraHigh"
            />
            <FuelConsumptionSection
                formPrefix="listing.vehicle.consumptionEmission.wltp.fuelConsumption.high"
                labelPrefix="finderVehicle:fields.listing.vehicleConsumptionEmissionWltpFuelConsumptionHigh"
            />
            <FuelConsumptionSection
                formPrefix="listing.vehicle.consumptionEmission.wltp.fuelConsumption.low"
                labelPrefix="finderVehicle:fields.listing.vehicleConsumptionEmissionWltpFuelConsumptionLow"
            />
            <FuelConsumptionSection
                formPrefix="listing.vehicle.consumptionEmission.wltp.fuelConsumption.medium"
                labelPrefix="finderVehicle:fields.listing.vehicleConsumptionEmissionWltpFuelConsumptionMedium"
            />
            <VehicleColumn>
                <FormFields.SwitchField
                    {...t('finderVehicle:fields.listing.vehicleFullServiceHistory', {
                        returnObjects: true,
                    })}
                    name="listing.vehicleFullServiceHistory"
                    {...yesNoSwitch}
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.DatePickerField
                    {...t('finderVehicle:fields.listing.vehicleProductionDate', {
                        returnObjects: true,
                    })}
                    name="listing.vehicle.productionDate"
                    disabled
                />
            </VehicleColumn>
            <LocalizeValueSection
                formPrefix="listing.vehicle.driveSide"
                labelPrefix="finderVehicle:fields.listing.vehicleDriveSide"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.modelGeneration"
                labelPrefix="finderVehicle:fields.listing.vehicleModelGeneration"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.usageType"
                labelPrefix="finderVehicle:fields.listing.vehicleUsageType"
            />
            <LocalizeValueSection
                formPrefix="listing.vehicle.condition"
                labelPrefix="finderVehicle:fields.listing.vehicleCondition"
            />
            <VehicleColumn>
                <FormFields.SwitchField
                    {...t('finderVehicle:fields.listing.warrantyPorscheApproved', {
                        returnObjects: true,
                    })}
                    name="listing.warranty.porscheApproved"
                    {...yesNoSwitch}
                    disabled
                />
            </VehicleColumn>
            <VehicleColumn>
                <FormFields.InputField
                    label={t('finderVehicle:fields.listing.updatedAt.label')}
                    name="listing.updatedAt"
                    disabled
                />
            </VehicleColumn>
        </Row>
    );
};

export default memo(VehicleDetails);
